import { OrganizationMetrics } from '@prisma/client'
import { Flex, Modal, Typography } from 'antd'
import { Users } from 'lucide-react'
import { AgentKey } from '~/agents/common/common/types'
import { useOrganizationContext } from '~/core/context'
import {
  OrganizationMetricsEmployeeConfig,
  OrganizationMetricsTaskConfig,
} from '~/features/organizationMetrics/common/objects'
import { OrganizationMetricsSummary } from '~/features/organizationMetrics/common/types'
import { MetricsAgent } from './components/MetricsAgent'
import { useLogic } from './hooks/useLogic'

type ModalProps = Omit<React.ComponentProps<typeof Modal>, 'children'>

type Props = {
  organizationMetrics: OrganizationMetrics
} & ModalProps

export const OrganizationMetricsModal: React.FC<Props> = ({
  organizationMetrics,
  ...props
}) => {
  const logic = useLogic({ organizationMetrics })
  const { organizationMembers } = useOrganizationContext()

  const taskKeys = Object.keys(organizationMetrics?.tasks ?? {})

  const employeeKeys = taskKeys?.map(
    key => OrganizationMetricsTaskConfig[key]?.employee,
  )

  const employeeAppKeys = employeeKeys?.map(
    employee => OrganizationMetricsEmployeeConfig[employee]?.appKey,
  )

  const agents =
    organizationMembers?.data.filter(
      member =>
        member.appSession?.status === 'ACTIVE' &&
        employeeAppKeys?.includes(member.appSession?.app?.key as AgentKey),
    ) ?? []

  const aiEmployeesWorking = agents?.length

  const summary = organizationMetrics?.summary as OrganizationMetricsSummary

  return (
    <Modal width={600} footer={null} {...props}>
      <Flex vertical align="center" justify="center" className="w-full h-full">
        <Flex className="w-full">
          <Typography.Title level={3}>
            Your AI Employees are working hard 💪
          </Typography.Title>
        </Flex>
        <Flex justify="space-around" className="w-full mb-4">
          <Flex vertical className="w-full" align="center">
            <Typography.Text
              type="success"
              style={{ fontSize: 24, fontWeight: 600 }}
            >
              {logic.getCountWithUnit(summary?.timeSaved)}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Saved this month
            </Typography.Text>
          </Flex>
          <Flex vertical className="w-full" align="center">
            <Flex align="center" gap={4}>
              <Users size={24} color="grey" />
              <Typography.Text style={{ fontSize: 24, fontWeight: 600 }}>
                {aiEmployeesWorking}/6
              </Typography.Text>
            </Flex>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              AI employees working
            </Typography.Text>
          </Flex>
        </Flex>
        <Flex vertical className="w-full" gap={32}>
          {agents?.map(member => (
            <MetricsAgent
              member={member}
              organizationMetrics={organizationMetrics}
              key={member.id}
            />
          ))}
        </Flex>
      </Flex>
    </Modal>
  )
}
